import React, { Component } from 'react';
import { first, get } from 'lodash';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import Purchase from '../lib/purchase';
import Payment from '../lib/payment';
import { Time } from '../../components/Time';
import LinkWithPopover from './link-with-popover';
import { formatMessage } from '../../lib/intl';

const i18n = defineMessages({
  reserved: {
    id: 'purchaseInventoryStatus.status.reserved',
    defaultMessage: 'Reserved',
  },
  risk: {
    id: 'purchaseInventoryStatus.status.risk',
    defaultMessage: 'At risk',
  },
  pending: {
    id: 'purchaseInventoryStatus.status.pending',
    defaultMessage: 'Pending',
  },
  notReserved: {
    id: 'purchaseInventoryStatus.status.notReserved',
    defaultMessage: 'Not reserved',
  },
  seeTermsLink: {
    id: 'purchaseInventoryStatus.popoverContent.seeTermsLink',
    defaultMessage: 'See all terms.'
  },
  reserveUponFullPaymentText: {
    id: 'purchaseInventoryStatus.popoverContent.reserveUponFullPayment',
    defaultMessage: 'The inventory is only reserved once the group\'s full payment is made.'
  },
  paymentPastDueText: {
    id: 'purchaseInventoryStatus.popoverContent.paymentPastDue',
    defaultMessage: `You did not make the most recent payment on time.
      Your purchase and payment(s) to date are at risk per the {merchant}
      policies unless they grant you an extension to make the most recent payment.`,
  },
  nonePaidSppText: {
    id: 'purchaseInventoryStatus.popoverContent.multiPayment.nonePaid.specifiedPerPerson',
    defaultMessage: `The inventory is only reserved once your group's first
      {percentage}% payment has been sent and accepted by {merchant}.`,
  },
  nonePaidEvenSplitText: {
    id: 'purchaseInventoryStatus.popoverContent.multiPayment.nonePaid.evenSplit',
    defaultMessage: `The inventory is only reserved once your group's first {percentage}%
      has been sent and accepted by {merchant}, which the organizer can trigger once
      {slots} have contributed.`,
  },
  notPaidSppText: {
    id: 'purchaseInventoryStatus.popoverContent.singlePayment.notPaid.specifiedPerPerson',
    defaultMessage: `The inventory is only reserved once the group's full payment is made,
      which the organizer can trigger once enough contributions are made.`,
  },
  notPaidEvenSplit: {
    id: 'purchaseInventoryStatus.popoverContent.singlePayment.notPaid.evenSplit',
    defaultMessage: `The inventory is only reserved once the group's full payment is made,
      which the organizer can trigger once {slots} have contributed.`,
  },
  why: {
    id: 'purchaseInventoryStatus.why',
    defaultMessage: 'Why?',
  },
  terms: {
    id: 'purchaseInventoryStatus.terms',
    defaultMessage: 'Terms',
  },
  title: {
    id: 'purchaseInventoryStatus.title',
    defaultMessage: 'Inventory Status',
  },
});

class PurchaseInventoryStatus extends Component {
  constructor(props) {
    super(props);

    this.formatMessage = formatMessage(this.props.intl);
  }
  get statusCopy() {
    const { purchase } = this.props;
    const { payments, nextPaymentDueDeadline } = purchase;
    const nextDueDeadline = nextPaymentDueDeadline && new Date(nextPaymentDueDeadline);
    const allPaid = payments.every(p => Payment.isPaid(p.status));
    const noPaid = payments.every(p => !Payment.isPaid(p.status));
    const anyAuthorized = payments.some(p => Payment.isAuthorized(p.status));
    let copy = '';
    const now = new Date();

    if (Payment.isPaid(payments[0].status)) {
      copy = this.formatMessage(i18n.reserved);

      if (!allPaid && nextDueDeadline && nextDueDeadline <= now) {
        copy = this.formatMessage(i18n.risk);
      }
    }

    if (noPaid) {
      copy = this.formatMessage(anyAuthorized ? i18n.pending : i18n.notReserved);
    }

    return copy;
  }

  get popoverCopyTemplates() {
    const { purchase } = this.props;
    const { payments, productOrGroupMinSlots, nextPaymentDueDeadline, merchant } = purchase;
    const currentOrNextPayment = Purchase.getCurrentOrLastPayment(purchase);
    const nextDueDeadline = nextPaymentDueDeadline && new Date(nextPaymentDueDeadline);
    const date = (<Time time={nextDueDeadline} />);

    return {
      partiallyPaid: (
        <div>
          <FormattedMessage
            id='purchaseInventoryStatus.popoverContent.partiallyPaid'
            defaultMessage="Reserved as long as next {percentage}%  of group's payment is made by {date}"
            values={{
              percentage: currentOrNextPayment.percentage,
              date,
            }}
          />
          {' '}
          <a href="#terms">{this.formatMessage(i18n.seeTermsLink)}</a>
        </div>
      ),
      paymentPastDue: (
        <div>{this.formatMessage(i18n.paymentPastDueText, { merchant: merchant.displayName })}</div>
      ),
      nonePaidSpp: (
        <div>
          {
            this.formatMessage(i18n.nonePaidSppText, {
              percentage: get(first(payments), 'percentage'),
              merchant: merchant.displayName,
            })
          }
        </div>
      ),
      nonePaidEvenSplit: (
        <div>
          {
            this.formatMessage(i18n.nonePaidEvenSplitText, {
              percentage: get(first(payments), 'percentage'),
              merchant: merchant.displayName,
              slots: productOrGroupMinSlots,
            })
          }
        </div>
      ),
      notPaidSpp: (<div>{this.formatMessage(i18n.notPaidSppText)}</div>),
      notPaidEvenSplit: (
        <div>{this.formatMessage(i18n.notPaidEvenSplit, { slots: productOrGroupMinSlots })}</div>
      ),
      reserveUponFullPayment: (<div>{this.formatMessage(i18n.reserveUponFullPaymentText)}</div>),
    };
  }

  get nextDueDeadline() {
    const { purchase: { nextPaymentDueDeadline } } = this.props;
    return nextPaymentDueDeadline && new Date(nextPaymentDueDeadline);
  }

  get hasPaymentsPaid() {
    const { purchase: { payments } } = this.props;
    return payments.some(p => Payment.isPaid(p.status));
  }

  get hasUnpaidPayments() {
    const { purchase: { payments } } = this.props;
    return !payments.every(p => Payment.isPaid(p.status));
  }

  get isNextPaymentPastDue() {
    const now = new Date();
    return this.hasPaymentsPaid && this.hasUnpaidPayments &&
      this.nextDueDeadline && this.nextDueDeadline <= now;
  }

  get isPartiallyPaid() {
    return this.hasPaymentsPaid && this.hasUnpaidPayments;
  }

  get isUnpaidMultiPayment() {
    const { purchase: { payments } } = this.props;
    return this.hasUnpaidPayments && Purchase.isMultiPayment(payments)
  }

  get isUnpaidSinglePayment() {
    const { purchase: { payments } } = this.props;
    return this.hasUnpaidPayments && Purchase.isSinglePayment(payments);
  }

  popoverContent() {
    const { purchase } = this.props;
    const isSpecifiedPerPerson = Purchase.isSpecifiedPerPerson(purchase.group.splitType);
    const isEvenSplit = Purchase.isEvenSplit(purchase.group.splitType);

    if (this.isNextPaymentPastDue) return this.popoverCopyTemplates.paymentPastDue;

    if (this.isPartiallyPaid) return this.popoverCopyTemplates.partiallyPaid;

    if (this.isUnpaidMultiPayment) {
      if (isSpecifiedPerPerson) return this.popoverCopyTemplates.nonePaidSpp;
      return this.popoverCopyTemplates.nonePaidEvenSplit;
    }

    if (this.isUnpaidSinglePayment) {
      if (isSpecifiedPerPerson) return this.popoverCopyTemplates.notPaidSpp;
      if (isEvenSplit) return this.popoverCopyTemplates.notPaidEvenSplit;
    }

    return this.popoverCopyTemplates.reserveUponFullPayment;
  }

  statusPopover() {
    const { purchase } = this.props;
    const { payments, nextPaymentDueDeadline } = purchase;
    const allPaid = payments.every(p => Payment.isPaid(p.status));
    const nextDueDeadline = nextPaymentDueDeadline && new Date(nextPaymentDueDeadline);
    const now = new Date();
    let copy = this.formatMessage(i18n.why);

    if (Payment.isPaid(payments[0].status) && !allPaid) {
      copy = this.formatMessage(i18n.terms);

      if (nextDueDeadline && nextDueDeadline <= now) {
        copy = this.formatMessage(i18n.why);
      }
    }

    return (
      <LinkWithPopover name="purchase-inventory-details" text={copy}>
        <div>{this.popoverContent()}</div>
      </LinkWithPopover>
    );
  }

  render() {
    const { payments } = this.props.purchase;
    const allPaid = payments.every(p => Payment.isPaid(p.status));

    return (
      <div className="inventory-status">
        <span className="small-print">
          <i className="fa fa-cubes fa-fw" aria-hidden="true" />
          {' '}
          {this.formatMessage(i18n.title)}
        </span>
        <p>
          {this.statusCopy}
          <br />
          {allPaid || (
            <span className="small-print">
              {this.statusPopover()}
            </span>
          )}
        </p>
      </div>
    );
  }
}

PurchaseInventoryStatus.defaultProps = {
  purchase: null,
};

export default injectIntl(PurchaseInventoryStatus);



// WEBPACK FOOTER //
// ./src/desktop/components/purchase-inventory-status.js